import { useState } from "react";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MonthPicker } from "../components/MonthPicker";
import { TransactionTable } from "../components/TransactionTable";
import { matchesTypeFilter } from "../types";
import type { Transaction, MainCategory, TransactionTypeFilter } from "../types";
import { useCategories } from "../hooks/useCategories";
import { bankLabels } from "../lib/format";

const TYPE_FILTERS: Array<{ value: TransactionTypeFilter; label: string; title: string }> = [
  { value: "all", label: "Alla", title: "Allt utom överföringar" },
  { value: "counted", label: "Räknas", title: "Inkomster + utgifter som ingår i översikten" },
  { value: "expenses", label: "Utgifter", title: "Utgifter som räknas i totalsummor" },
  { value: "income", label: "Inkomster", title: "Alla inkomster" },
  { value: "shown_only", label: "Visas bara", title: "Utgifter från källor som inte räknas" },
  { value: "transfers", label: "Överföringar", title: "Endast överföringar (räknas aldrig)" },
];

interface TransactionsPageProps {
  transactions: Transaction[];
  availableMonths: string[];
  selectedMonth: string | null;
  typeFilter: TransactionTypeFilter;
  onTypeFilterChange: (filter: TransactionTypeFilter) => void;
  onMonthChange: (month: string | null) => void;
  onUpdateCategory: (
    id: string,
    category: MainCategory,
    subcategory: string
  ) => Promise<void>;
}

export function TransactionsPage({
  transactions,
  availableMonths,
  selectedMonth,
  typeFilter,
  onTypeFilterChange,
  onMonthChange,
  onUpdateCategory,
}: TransactionsPageProps) {
  const { allCategories } = useCategories();
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [bankFilter, setBankFilter] = useState<string>("all");

  let filtered = transactions.filter((t) => matchesTypeFilter(t, typeFilter));

  if (categoryFilter !== "all") {
    filtered = filtered.filter((t) => t.category === categoryFilter);
  }

  if (bankFilter !== "all") {
    filtered = filtered.filter((t) => t.bank === bankFilter);
  }

  const banks = [...new Set(transactions.map((t) => t.bank))];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-2xl font-bold tracking-tight">Transaktioner</h2>
        <MonthPicker
          value={selectedMonth}
          availableMonths={availableMonths}
          onChange={onMonthChange}
        />
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {TYPE_FILTERS.map((filter) => (
          <button
            key={filter.value}
            title={filter.title}
            onClick={() => onTypeFilterChange(filter.value)}
            className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
              typeFilter === filter.value
                ? "border-primary bg-primary text-primary-foreground"
                : "text-muted-foreground hover:border-primary/40 hover:text-foreground"
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Kategori" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla kategorier</SelectItem>
            {allCategories
              .filter((c) => c.value !== "transfer")
              .map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>

        <Select value={bankFilter} onValueChange={setBankFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Bank" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla banker</SelectItem>
            {banks.map((bank) => (
              <SelectItem key={bank} value={bank}>
                {bankLabels[bank] ?? bank}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <span className="text-sm text-muted-foreground ml-auto">
          {filtered.length} transaktioner
        </span>
      </div>

      <Card>
        <CardContent className="pt-6">
          <TransactionTable
            transactions={filtered}
            onUpdateCategory={onUpdateCategory}
          />
        </CardContent>
      </Card>
    </div>
  );
}
